import { Property } from '../../services/api';

/**
 * 커스텀 그리드 클러스터링 (Phase 9)
 *
 * 카카오 CustomOverlay 마커는 표준 MarkerClusterer 로 묶을 수 없어(표준 Marker 전용)
 * 위경도 그리드 셀 기반으로 직접 클러스터링한다.
 *
 * 규칙:
 *  - 특별현장(is_special)은 절대 클러스터에 넣지 않고 항상 개별(single)로 반환한다.
 *  - 나머지 일반현장을 레벨별 셀 크기로 버킷팅해 셀 내 2개 이상이면 클러스터, 1개면 개별.
 *  - 순수 함수(부수효과 없음) — 단위 테스트 가능.
 */

export interface Cluster {
  lat: number;
  lng: number;
  count: number;
  memberIds: number[];
}

export interface ClusterResult {
  clusters: Cluster[];
  singles: Property[];
}

/**
 * 레벨별 그리드 셀 크기(위경도 도 단위).
 * 카카오 지도 레벨: 1(최대 줌인) ~ 14(최대 줌아웃). 줌아웃일수록 셀을 크게 잡아 더 많이 묶는다.
 * 레벨 5 기준 약 0.0016도(≈180m), 레벨이 1 오를 때마다 2배.
 */
export function getCellSizeDegrees(level: number): number {
  const safeLevel = Number.isFinite(level) ? level : 8;
  return 0.0016 * Math.pow(2, safeLevel - 5);
}

/**
 * @param properties 클러스터링 대상 현장 목록(화면 내 현장 등, 좌표 없는 항목은 무시)
 * @param level 현재 카카오 지도 레벨
 */
export function clusterProperties(properties: Property[], level: number): ClusterResult {
  const cellSize = getCellSizeDegrees(level);
  const singles: Property[] = [];
  const buckets = new Map<string, Property[]>();

  for (const p of properties) {
    // 특별현장은 항상 개별 — 클러스터 버킷에 넣지 않는다.
    // 배너 마커(활성 혜택 현장)도 항상 개별 — 배너 수 = 활성 혜택 현장 수 보장 (v2b 2026-09-21)
    if (p.is_special || p.has_active_benefit === true) {
      singles.push(p);
      continue;
    }
    if (typeof p.latitude !== 'number' || typeof p.longitude !== 'number') {
      continue;
    }
    const cellX = Math.floor(p.longitude / cellSize);
    const cellY = Math.floor(p.latitude / cellSize);
    const key = `${cellX}:${cellY}`;
    const bucket = buckets.get(key);
    if (bucket) bucket.push(p);
    else buckets.set(key, [p]);
  }

  const clusters: Cluster[] = [];
  buckets.forEach((members) => {
    if (members.length >= 2) {
      let sumLat = 0;
      let sumLng = 0;
      for (const m of members) {
        sumLat += m.latitude as number;
        sumLng += m.longitude as number;
      }
      clusters.push({
        lat: sumLat / members.length,
        lng: sumLng / members.length,
        count: members.length,
        memberIds: members.map((m) => m.id),
      });
    } else {
      singles.push(members[0]);
    }
  });

  return { clusters, singles };
}
